import type { ReactNode } from 'react';

type Accent = 'primary' | 'success' | 'warning' | 'error' | 'info';

const accentBorder: Record<Accent, string> = {
  primary: 'border-t-primary-600',
  success: 'border-t-success',
  warning: 'border-t-warning',
  error: 'border-t-error',
  info: 'border-t-info',
};

const accentIconBg: Record<Accent, string> = {
  primary: 'bg-gradient-to-br from-primary-50 to-primary-100 text-primary-800 ring-1 ring-inset ring-primary-100',
  success: 'bg-success-bg text-success ring-1 ring-inset ring-success-border/50',
  warning: 'bg-warning-bg text-warning ring-1 ring-inset ring-warning-border/50',
  error: 'bg-error-bg text-error ring-1 ring-inset ring-error-border/50',
  info: 'bg-info-bg text-info ring-1 ring-inset ring-info-border/50',
};

// A single premium stat-tile primitive (top accent border + big number + label),
// so every dashboard in the app renders KPI numbers the same way.
export function StatCard({
  label,
  value,
  icon,
  accent = 'primary',
  footer,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  accent?: Accent;
  footer?: ReactNode;
}) {
  return (
    <div
      className={`bg-white rounded-2xl border border-border-subtle border-t-[3px] ${accentBorder[accent]} shadow-card hover:shadow-card-hover transition-shadow duration-300 ease-premium p-4`}
    >
      <div className="flex items-start justify-between mb-2.5">
        <p className="text-[11px] font-semibold tracking-wide uppercase text-ink-faint">{label}</p>
        {icon && (
          <div className={`w-8 h-8 flex items-center justify-center rounded-[10px] flex-shrink-0 ${accentIconBg[accent]}`}>
            {icon}
          </div>
        )}
      </div>
      <p className="text-[27px] font-extrabold text-ink leading-tight tracking-tight tabular-nums">{value}</p>
      {footer && <div className="mt-2 text-[11.5px] text-ink-faint">{footer}</div>}
    </div>
  );
}
